import React, { useEffect, useState } from "react";
import { Tabs, Empty } from "antd";
import useMessageTemplates from "hooks/message-templates/useMessageTemplates";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner, Card, Button } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import HtmlEditor from "components/editors/HTMLEditor";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";

const CodeBadge = ({ code }) => {
  const style = {
    backgroundColor: "#e0f0ff",
    color: "#1565c0",
    padding: "5px 8px",
    borderRadius: "2px",
    fontSize: "0.65rem",
    border: "1px solid #b3d4fc",
    cursor: "default",
    fontWeight: 600,
  };

  return <span style={style}>{code}</span>;
}

export default function MessageTemplateView() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { fetchTemplateById } = useMessageTemplates();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getTemplate = async () => {
      setLoading(true);
      try {
        const data = await fetchTemplateById(templateId);
        setTemplate(data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    getTemplate();
  }, [templateId]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
        <WidgetSectionTitle
          icon="envelope-open-text"
          title="Message Template"
          subtitle="Define a new email or SMS template"
          className="flex-1"
        />

        <div>
          <Button
            variant="secondary"
            size="sm"
            className="text-sm me-2"
            onClick={() => navigate(`/account/message-templates`)}
          >
            Go Back
          </Button>

          <Guard privilege={PRIV.UPDATE_TEMPLATES}>
            <Button
              variant="info"
              size="sm"
              className="text-sm"
              onClick={() =>
                navigate(`/account/message-templates/update/${templateId}`)
              }
            >
              Edit Template
            </Button>
          </Guard>
        </div>
      </div>

      <div className="row g-3">
        {/* LEFT */}
        <div className="col-lg-4">
          <Card
            title="Template Details"
            className="custom-shadow p-3 flex-fill"
          >
            <div className="mb-3">
              <p className="text-xxs font-inter text-secondary mb-0">Code</p>
              <div>{template && <CodeBadge code={template.code} />}</div>
            </div>

            <div className="mb-3">
              <p className="text-xxs font-inter text-secondary mb-0">
                Client App
              </p>
              <p className="text-xs text-muted mb-0">{template?.clientApp}</p>
            </div>

            <div className="mb-3">
              <p className="text-xxs font-inter text-secondary mb-0">Locale</p>
              <p className="text-xs text-muted mb-0">{template?.locale}</p>
            </div>

            <div className="mb-3">
              <p className="text-xxs font-inter text-secondary mb-0">Status</p>
              <div>
                {template && (
                  <span
                    className="py-1 px-2"
                    style={{
                      backgroundColor: template.active ? "#e8f7e3" : "#fbe9e7",
                      color: template.active ? "#2e7d32" : "#c62828",
                      padding: "3px 8px",
                      borderRadius: "2px",
                      fontSize: "0.65rem",
                      border: template.active
                        ? "1px solid #d5ebd0"
                        : "1px solid #f5c6cb",
                      cursor: "default",
                      fontWeight: 600,
                    }}
                  >
                    {template.active ? "Active" : "Disabled"}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-0 mb-2">
              <p className="text-xxs font-inter text-secondary mb-0">Subject</p>
              <p className="text-xs text-muted mb-0">
                {template?.subject || "No subject"}
              </p>
            </div>
          </Card>
        </div>

        {/* RIGHT */}
        <div className="col-lg-8 d-flex">
          <Card className="custom-shadow flex-fill">
            <Tabs
              tabBarStyle={{
                padding: "0px 16px",
                paddingTop: "8px",
                borderBottom: "1px solid #E2E8F0",
                marginBottom: "0px",
                fontFamily: "Inter",
              }}
              styles={{
                indicator: {
                  backgroundColor: "#00D27A",
                },
              }}
              style={{ margin: 0 }}
              items={[
                {
                  key: "html",
                  label: "HTML Preview",
                  children: template?.bodyHtml ? (
                    <div
                      style={{
                        overflow: "hidden",
                        padding: 10,
                      }}
                      dangerouslySetInnerHTML={{ __html: template.bodyHtml }}
                    />
                  ) : (
                    <div
                      style={{
                        minHeight: 200,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Empty description="No HTML content available" />
                    </div>
                  ),
                },
                {
                  key: "text",
                  label: "Text Version",
                  children: (
                    <div className="p-3">
                      <pre style={{ whiteSpace: "pre-wrap" }}>
                        {template?.bodyText}
                      </pre>
                    </div>
                  ),
                },
                {
                  key: "raw",
                  label: "Raw HTML",
                  children: template?.bodyHtml ? (
                    <HtmlEditor
                      value={template?.bodyHtml || ""}
                      readOnly={true}
                      height={600}
                    />
                  ) : (
                    <div
                      style={{
                        minHeight: 200,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Empty description="No HTML content available" />
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </div>
      </div>
    </>
  );
}
